import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-primary-50/50 text-primary-700 border-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800',
        secondary: 'bg-secondary-50/50 text-secondary-700 border-secondary-200 dark:bg-secondary-900/20 dark:text-secondary-300 dark:border-secondary-800',
        success: 'bg-success-light/20 text-success-dark border-success-light dark:bg-success-dark/20 dark:text-success-light dark:border-success-dark',
        warning: 'bg-warning-light/20 text-warning-dark border-warning-light dark:bg-warning-dark/20 dark:text-warning-light dark:border-warning-dark',
        error: 'bg-error-light/20 text-error-dark border-error-light dark:bg-error-dark/20 dark:text-error-light dark:border-error-dark',
        mint: 'bg-accent-mint/20 text-primary-700 border-accent-mint dark:bg-accent-mint/10 dark:text-primary-300 dark:border-accent-mint/50',
        rose: 'bg-accent-rose/20 text-secondary-700 border-accent-rose dark:bg-accent-rose/10 dark:text-secondary-300 dark:border-accent-rose/50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  onClose?: (() => void) | undefined;
}

export function Alert({
  className,
  variant,
  icon,
  title,
  description,
  action,
  onClose,
  children,
  ...props
}: AlertProps) {
  return (
    <div className={alertVariants({ variant, className })} {...props} role="alert">
      {icon}
      <div className="flex justify-between gap-4">
        <div>
          {title && <h5 className="mb-1 font-medium leading-none tracking-tight">{title}</h5>}
          {description && <div className="text-sm opacity-90">{description}</div>}
          {children}
        </div>
        {(action || onClose) && (
          <div className="ml-auto flex items-center gap-2">
            {action}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                aria-label="Close"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 